(() => {
  'use strict';
  if (customElements.get('tf-seat-cover-configurator')) return;
  const esc = value => String(value ?? '').replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;');
  class SeatCoverConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-tfsc-config]').textContent);
        this.product = this.config.product;
        this.variants = this.product.variants;
        if (!Array.isArray(this.variants) || !this.variants.length) throw new Error('Product variants are unavailable.');
        this.colors = [...new Set(this.variants.map(v => v.option1))];
        this.packages = [...new Set(this.variants.map(v => v.option2))];
        const initial = this.variants.find(v => v.available) || this.variants[0];
        this.color = initial.option1;
        this.coverage = initial.option2;
        this.requestId = 0;
        this.render();
        this.bind();
        this.syncVariant();
        this.loadYears();
      } catch (error) { this.textContent = 'The seat-cover configurator could not be loaded. Please refresh the page.'; }
    }
    money(cents) {
      return new Intl.NumberFormat('en-US',{style:'currency',currency:this.config.currency || 'USD'}).format(Number(cents) / 100);
    }
    imageOf(variant) {
      const image = variant?.featured_image;
      return (typeof image === 'string' ? image : image?.src) || this.product.featured_image || this.product.images?.[0] || '';
    }
    render() {
      const fields = [['Year','year'],['Make','make'],['Model','model'],['Submodel','trim'],['Cab Size','cab']];
      this.innerHTML = `<div class="tfsc-layout"><div class="tfsc-gallery"><img data-main-image alt="Seat cover configuration" loading="eager"><p class="tfsc-caption" data-image-caption></p></div><div class="tfsc-info"><h1>${esc(this.product.title)}</h1><div class="tfsc-prices"><strong data-price></strong><s data-compare hidden></s></div><p class="tfsc-intro">Personalise your seat covers for your vehicle, colour and seat setup.</p><form class="tfsc-form" method="post"><input type="hidden" name="id" data-variant-id><fieldset class="tfsc-step"><legend>1. Your vehicle</legend><p class="tfsc-hint">Choose your year, make and model.</p><div class="tfsc-vehicle-grid">${fields.map(([label,key])=>`<label class="tfsc-field" data-field="${key}" ${['trim','cab'].includes(key)?'hidden':''}><span>${label}</span><select name="properties[${label}]" data-select="${key}" aria-label="${label}" disabled><option value="">Select ${label}</option></select></label>`).join('')}</div><p class="tfsc-hint tfsc-api-status" data-vehicle-status role="status"></p><button type="button" class="tfsc-retry" data-retry hidden>Retry vehicle lookup</button></fieldset><fieldset class="tfsc-step"><legend>2. Colour</legend><div class="tfsc-colours">${this.colors.map((color,index)=>`<button type="button" class="tfsc-colour" data-color="${index}" aria-pressed="false"><img src="${esc(this.imageOf(this.variants.find(v=>v.option1===color)))}" alt="" loading="lazy"><span>${esc(color)}</span></button>`).join('')}</div></fieldset><fieldset class="tfsc-step"><legend>3. Seat package</legend><div class="tfsc-packages">${this.packages.map((value,index)=>`<button type="button" data-package="${index}" aria-pressed="false"><span>${esc(value)}</span><strong data-package-price="${index}"></strong></button>`).join('')}</div></fieldset><div class="tfsc-buy"><label class="tfsc-quantity">Quantity<input name="quantity" type="number" min="1" max="100" step="1" value="1" required></label><button type="submit" class="tfsc-submit" disabled>Add to cart</button></div><p class="tfsc-status" data-stock-status role="status"></p><p class="tfsc-error" data-error role="alert" hidden></p><div class="tfsc-result" data-result hidden></div></form></div></div>${this.config.showDescription!==false?`<div class="tfsc-description">${this.product.description || ''}</div>`:''}`;
      this.form = this.querySelector('form');
      this.selects = Object.fromEntries(fields.map(([,key])=>[key,this.querySelector(`[data-select="${key}"]`)]));
      this.addButton = this.querySelector('.tfsc-submit');
      ['year','make','model'].forEach(key=>{this.selects[key].required=true;});
    }
    bind() {
      this.querySelectorAll('[data-color]').forEach(button=>button.addEventListener('click',()=>{
        this.color=this.colors[Number(button.dataset.color)]; this.syncVariant();
      }));
      this.querySelectorAll('[data-package]').forEach(button=>button.addEventListener('click',()=>{
        this.coverage=this.packages[Number(button.dataset.package)]; this.syncVariant();
      }));
      this.selects.year.addEventListener('change',()=>this.loadMakes());
      this.selects.make.addEventListener('change',()=>this.loadModels());
      this.selects.model.addEventListener('change',()=>this.loadTrims());
      this.selects.trim.addEventListener('change',()=>this.loadCabs());
      this.selects.cab.addEventListener('change',()=>this.syncReady());
      this.querySelector('[data-retry]').addEventListener('click',()=>this.retry?.());
      this.form.addEventListener('submit',event=>this.submit(event),true);
    }
    reset(keys) {
      for (const key of keys) {
        const select=this.selects[key]; select.replaceChildren(new Option(`Select ${key==='trim'?'Submodel':key==='cab'?'Cab Size':key[0].toUpperCase()+key.slice(1)}`,'')); select.disabled=true;
        if (['trim','cab'].includes(key)) { select.required=false; this.querySelector(`[data-field="${key}"]`).hidden=true; }
      }
      this.querySelector('[data-result]').hidden=true;
      this.querySelector('[data-error]').hidden=true;
      this.syncReady();
    }
    populate(key, values) {
      const select=this.selects[key];
      const items=[...new Set(values.map(String))].filter(Boolean);
      for (const value of items) select.add(new Option(value,value));
      select.disabled=!items.length;
      if (['trim','cab'].includes(key) && items.length) { select.required=true; this.querySelector(`[data-field="${key}"]`).hidden=false; }
    }
    async lookup(params) {
      const endpoint=new URL(this.config.endpoint);
      if (endpoint.protocol!=='https:') throw new Error('Vehicle data requires a secure connection.');
      endpoint.searchParams.set('type','normal'); endpoint.searchParams.set('with_sales','yes');
      for (const [key,value] of Object.entries(params)) endpoint.searchParams.set(key,value);
      const response=await fetch(endpoint.href,{signal:AbortSignal.timeout(20000)});
      if(!response.ok) throw new Error('Vehicle data is temporarily unavailable.');
      return response.json();
    }
    status(message, retry=null) {
      this.querySelector('[data-vehicle-status]').textContent=message;
      this.retry=retry; this.querySelector('[data-retry]').hidden=!retry;
    }
    async loadYears() {
      this.reset(['year','make','model','trim','cab']);
      const id=++this.requestId; this.status('Loading available years…');
      try {
        const data=await this.lookup({}); if(id!==this.requestId)return;
        this.populate('year',data.years || []);
        this.status(this.selects.year.disabled?'No vehicle years are available.':'Select your vehicle year.');
      } catch(error) {if(id===this.requestId)this.status('Vehicle data is temporarily unavailable.',()=>this.loadYears());}
    }
    async loadMakes() {
      this.reset(['make','model','trim','cab']); this.modelData=null;
      const id=++this.requestId, year=this.selects.year.value;
      if(!year){this.status('Select your vehicle year.');return;}
      this.status('Loading makes…');
      try {
        const data=await this.lookup({year});if(id!==this.requestId)return;
        const priority=['Ford','Ram','Chevrolet','GMC','Dodge','Toyota'];
        const makes=[...(data.makes || [])].sort((a,b)=>{
          const ai=priority.includes(a)?priority.indexOf(a):99,bi=priority.includes(b)?priority.indexOf(b):99;
          return ai-bi || a.localeCompare(b);
        });
        this.populate('make',makes);this.status(makes.length?'Select your vehicle make.':'No makes were returned for this year.');
      } catch(error){if(id===this.requestId)this.status('Could not load makes. Please retry.',()=>this.loadMakes());}
    }
    async loadModels() {
      this.reset(['model','trim','cab']); this.modelData=null;
      const id=++this.requestId,year=this.selects.year.value,make=this.selects.make.value;
      if(!make){this.status('Select your vehicle make.');return;}
      this.status('Loading models…');
      try {
        const data=await this.lookup({year,make});if(id!==this.requestId)return;
        this.modelData=data;const models=Object.keys(data.models || {});
        this.populate('model',models);this.status(models.length?'Select your vehicle model.':'No models were returned for this make.');
      }catch(error){if(id===this.requestId)this.status('Could not load models. Please retry.',()=>this.loadModels());}
    }
    loadTrims() {
      this.reset(['trim','cab']);
      const model=this.selects.model.value;
      if(!model){this.status('Select your vehicle model.');return;}
      const entries=this.modelData?.trims2?.[model] || [];
      this.populate('trim',entries.map(entry=>entry.trim).filter(Boolean));
      this.status(this.selects.trim.required?'Select the submodel for your vehicle.':'Vehicle selected.');this.syncReady();
    }
    loadCabs() {
      this.reset(['cab']);
      const entries=this.modelData?.trims2?.[this.selects.model.value] || [];
      this.populate('cab',entries.filter(entry=>entry.trim===this.selects.trim.value).map(entry=>entry.cab).filter(Boolean));
      this.status(this.selects.cab.required?'Select your cab size.':'Vehicle selected.');this.syncReady();
    }
    vehicleReady() {
      return ['year','make','model','trim','cab'].every(key=>!this.selects[key].required || (!this.selects[key].disabled && this.selects[key].value));
    }
    syncVariant() {
      this.variant=this.variants.find(v=>v.option1===this.color && v.option2===this.coverage);
      this.querySelector('[data-variant-id]').value=this.variant?.id || '';
      this.querySelector('[data-price]').textContent=this.variant?this.money(this.variant.price):'Unavailable';
      const compare=this.querySelector('[data-compare]');
      compare.hidden=!(this.variant?.compare_at_price > this.variant?.price);
      compare.textContent=compare.hidden?'':this.money(this.variant.compare_at_price);
      const image=this.querySelector('[data-main-image]');const url=this.imageOf(this.variant);
      if(url){image.src=url;image.alt=`${this.color} — ${this.coverage}`;}
      this.querySelector('[data-image-caption]').textContent=`${this.color} · ${this.coverage}`;
      this.querySelectorAll('[data-color]').forEach(button=>button.setAttribute('aria-pressed',String(this.colors[Number(button.dataset.color)]===this.color)));
      this.querySelectorAll('[data-package]').forEach(button=>{
        const value=this.packages[Number(button.dataset.package)];const v=this.variants.find(v=>v.option1===this.color && v.option2===value);
        button.setAttribute('aria-pressed',String(value===this.coverage));button.disabled=!v?.available;
        button.querySelector('strong').textContent=v?this.money(v.price):'Unavailable';
      });
      this.syncReady();
    }
    syncReady() {
      if(!this.addButton)return;
      const available=Boolean(this.variant?.available),ready=this.vehicleReady();
      this.addButton.disabled=this.busy || !available || !ready;
      this.addButton.textContent=this.busy?'Adding…':available?'Add to cart':'Sold out';
      this.querySelector('[data-stock-status]').textContent=!available?'This seat-cover configuration is unavailable.':ready?'Your configuration is ready.':'Complete your vehicle details to continue.';
    }
    async submit(event) {
      event.preventDefault();event.stopImmediatePropagation();
      if(this.busy || !this.vehicleReady() || !this.variant?.available || !this.form.reportValidity())return;
      const quantity=Number(this.form.elements.quantity.value);
      if(!Number.isInteger(quantity) || quantity<1 || quantity>100)return;
      const properties={};
      for(const select of Object.values(this.selects)) if(!select.disabled && select.value)properties[select.name.slice(11,-1)]=select.value;
      const payload={items:[{id:this.variant.id,quantity,properties}]};
      const error=this.querySelector('[data-error]');error.hidden=true;
      if(this.config.preview){
        const result=this.querySelector('[data-result]');result.hidden=false;
        result.innerHTML=`<h3>Configuration preview</h3><p>${esc(this.color)} · ${esc(this.coverage)} · Quantity ${quantity}</p><dl>${Object.entries(properties).map(([key,value])=>`<dt>${esc(key)}</dt><dd>${esc(value)}</dd>`).join('')}</dl><p>This preview does not place an order.</p>`;
        this.dispatchEvent(new CustomEvent('tfsc:preview',{detail:payload}));return;
      }
      this.busy=true;this.syncReady();
      try {
        const response=await fetch(this.config.cartAddUrl,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},credentials:'same-origin',body:JSON.stringify(payload)});
        const data=await response.json();if(!response.ok)throw new Error(data.description || data.message || 'Please check your selection and try again.');
        window.location.assign(this.config.cartUrl);
      }catch(problem){error.textContent=problem.message || 'Could not add this item. Please try again.';error.hidden=false;this.busy=false;this.syncReady();}
    }
  }
  customElements.define('tf-seat-cover-configurator',SeatCoverConfigurator);
})();
